import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AuthSpinner } from "@/components/AuthSpinner";
import { homeForRole } from "@/lib/roleRoutes";

/**
 * Shown when the role lookup fails (network blip, RLS misconfig, etc.).
 * Lets the user retry the role fetch or sign out cleanly.
 */
export default function RoleError() {
  const { roleError, refreshRole, signOut, refreshing, role } = useAuth();
  const location = useLocation();
  const attempted = (location.state as { from?: string } | null)?.from;
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    toast.error("Couldn't verify your access", {
      description: "We had trouble loading your account permissions.",
    });
  }, []);

  // If the role recovers, bounce to the safe home.
  if (role) {
    return <Navigate to={homeForRole(role)} replace />;
  }

  if (refreshing || retrying) return <AuthSpinner label="Re-checking access…" />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <div className="max-w-md text-center">
        <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-destructive">
          Access check failed
        </p>
        <h1 className="mb-3 text-3xl font-bold">We couldn't load your account</h1>
        <p className="mb-2 text-muted-foreground">
          There was a problem verifying your permissions. Try again, or sign out and log
          back in.
        </p>
        {roleError && (
          <p className="mb-4 text-xs opacity-60">
            <code>{roleError}</code>
          </p>
        )}
        {attempted && (
          <p className="mb-4 text-xs opacity-60">
            You were trying to open <code>{attempted}</code>.
          </p>
        )}
        <div className="flex justify-center gap-3">
          <button
            onClick={async () => {
              setRetrying(true);
              await refreshRole();
              setRetrying(false);
            }}
            className="rounded-lg bg-primary px-5 py-2.5 font-semibold text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <button
            onClick={() => signOut()}
            className="rounded-lg border border-border bg-background px-5 py-2.5 font-semibold hover:bg-muted"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
